import { ref } from 'vue';
import { trackEvent as baseTrackEvent } from '@/common/analytics.js';
import { useRoute } from 'vue-router';
import { isCmdcTrafficSource } from '@/utils/channelSource.js';

/** 埋点 sourceSign：2 普通移动 H5，3 中国医疗器械入口（sessionStorage，任意页 ?source=cmdc 写入） */
function resolveSourceSign(route) {
	if (route?.query?.source === 'cmdc' || isCmdcTrafficSource()) return 3;
	return 2;
}

const defaultObserverOptions = {
	threshold: [0.01, 0.5, 1],
	rootMargin: '0px',
};

/**
 * 通用埋点 Composable，可在任意页面/组件中使用
 * @returns {{
 *   trackEvent: (options: Object) => Promise,
 *   track: (options: Object) => Promise,
 *   trackEventExposure: (selectorOrElement: string | Element, eventOptions: Object) => { disconnect: () => void }
 * }}
 */
export function useAnalytics() {
	const route = useRoute();
	const exposureObservers = ref([]);

	/**
	 * 埋点上报（仅客户端；可自动附带当前路由与 sourceSign）
	 * @param {Object} options - 与 common/analytics trackEvent 一致；若不传 route / sourceSign 则按当前路由自动补全
	 */
	function trackEvent(options = {}) {
		if (!import.meta.client) {
			return Promise.resolve();
		}
		const payload = { ...options };
		if (!payload.route && route?.path) {
			payload.route = route.path;
		}
		if (payload.sourceSign == null) {
			payload.sourceSign = resolveSourceSign(route);
		}
		return baseTrackEvent(payload);
	}

	/**
	 * 公共曝光监听：监听 DOM 进入视口后上报一次曝光埋点，并自动停止监听
	 * @param {string | Element} selectorOrElement - CSS 选择器（仅取第一个匹配）或 DOM 元素
	 * @param {Object} eventOptions - 曝光时上报的埋点参数（与 trackEvent 一致），如 module、eventCode、eventName、attrName、attrCode 等
	 * @param {Object} [observerOptions] - IntersectionObserver 配置，如 threshold、rootMargin
	 * @returns {{ disconnect: () => void }} 用于在 onUnmounted 时断开监听，避免泄漏
	 */
	function trackEventExposure(selectorOrElement, eventOptions, observerOptions = {}) {
		if (!import.meta.client) {
			return { disconnect: () => {} };
		}

		const opts = { ...defaultObserverOptions, ...observerOptions };
		let observer = null;
		const disconnect = () => {
			const obs = observer;
			observer = null;
			if (obs) obs.disconnect();
			const idx = exposureObservers.value.indexOf(obs);
			if (idx > -1) exposureObservers.value.splice(idx, 1);
		};

		const run = () => {
			if (typeof document === 'undefined') return disconnect;

			const el = typeof selectorOrElement === 'string' ? document.querySelector(selectorOrElement) : selectorOrElement;

			if (!el) return disconnect;

			observer = new IntersectionObserver(entries => {
				entries.forEach(entry => {
					if (!entry.isIntersecting || entry.intersectionRatio <= 0.01) return;
					trackEvent({ ...eventOptions });
					observer?.unobserve(entry.target);
					disconnect();
				});
			}, opts);

			observer.observe(el);
			exposureObservers.value.push(observer);
		};

		if (typeof document !== 'undefined' && document.readyState !== 'loading') {
			run();
		} else if (typeof document !== 'undefined') {
			document.addEventListener('DOMContentLoaded', run, { once: true });
		}

		return { disconnect };
	}

	/**
	 * 组件卸载时自动断开本 composable 创建的所有曝光监听（可选调用）
	 */
	function disconnectAllExposure() {
		exposureObservers.value.forEach(obs => obs?.disconnect?.());
		exposureObservers.value = [];
	}

	return {
		trackEvent,
		trackEventExposure,
		disconnectAllExposure,
	};
}
